// Iter 82.11 / Phase E3.2 Sprint 1 — Leadership Dashboard.
//
// "What's the headline cost trajectory + headcount mix?"
// Audience: CEO, Board. FYTD totals + headcount + cost-per-FTE + regime mix
// + department cost stack + 24-month trend.
import { useCallback, useEffect, useState } from "react";
import {
    ChartPieSlice, ChartBar, Users, ChartLineUp, ArrowsClockwise,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const compact = (n) => {
    const v = Number(n || 0);
    if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
    if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
    if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
    return inr(v);
};
const thisMonthYM = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function TrendBars({ series, testId }) {
    const max = Math.max(1, ...series.map((s) => s.gross_earnings || 0));
    return (
        <div className="flex items-end gap-1 h-32" data-testid={testId}>
            {series.map((s) => {
                const h = Math.max(2, (100 * (s.gross_earnings || 0)) / max);
                const tone = (s.gross_earnings || 0) > 0 ? "bg-indigo-500" : "bg-slate-200";
                return (
                    <div key={s.period_month} className="flex-1 flex flex-col items-center gap-1" title={`${s.period_month}: ${inr(s.gross_earnings)}`}>
                        <div className={`w-full rounded-t ${tone}`} style={{ height: `${h}%` }} />
                        <div className="text-[9px] text-slate-400 font-mono">{s.period_month.slice(2)}</div>
                    </div>
                );
            })}
        </div>
    );
}

function DeptStack({ rows }) {
    const max = Math.max(1, ...rows.map((r) => r.gross || 0));
    return (
        <div className="space-y-1.5" data-testid="lead-dept-stack">
            {rows.slice(0, 10).map((r) => {
                const pct = Math.max(1, (100 * (r.gross || 0)) / max);
                return (
                    <div key={r.department} className="flex items-center gap-2 text-xs">
                        <div className="w-32 truncate text-slate-600" title={r.department}>{r.department}</div>
                        <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden relative">
                            <div className="h-full bg-violet-400 transition-all" style={{ width: `${pct}%` }} />
                            <div className="absolute inset-0 flex items-center px-2 font-mono text-[10px] text-slate-700">
                                {compact(r.gross)} · {r.headcount}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function RegimePie({ mix }) {
    const total = (mix.total || 0) || 1;
    const old_pct = mix.old_pct || 0;
    const new_pct = mix.new_pct || 0;
    return (
        <div className="flex items-center gap-4" data-testid="lead-regime-mix">
            <div className="relative w-24 h-24">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    <circle cx="18" cy="18" r="15.9155" fill="transparent" stroke="#e2e8f0" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15.9155" fill="transparent" stroke="#6366f1" strokeWidth="3" strokeDasharray={`${old_pct} ${100 - old_pct}`} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-xs font-mono">
                    {mix.total || 0}
                </div>
            </div>
            <div className="text-sm">
                <div className="flex items-center gap-1.5" data-testid="lead-regime-old"><span className="w-2 h-2 bg-indigo-500 rounded"></span> Old: {mix.old_count || 0} ({old_pct}%)</div>
                <div className="flex items-center gap-1.5" data-testid="lead-regime-new"><span className="w-2 h-2 bg-slate-300 rounded"></span> New: {mix.new_count || 0} ({new_pct}%)</div>
                {mix.unknown_count > 0 && <div className="flex items-center gap-1.5 text-amber-700"><span className="w-2 h-2 bg-amber-300 rounded"></span> Unknown: {mix.unknown_count}</div>}
            </div>
        </div>
    );
}

export default function PayrollLeadershipDashboard() {
    const [periodMonth, setPeriodMonth] = useState(thisMonthYM());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/payroll/analytics/dashboard/leadership", {
                params: { period_month: periodMonth },
            });
            setData(r.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load leadership dashboard"));
        } finally {
            setLoading(false);
        }
    }, [periodMonth]);
    useEffect(() => { reload(); }, [reload]);

    if (loading) return <div className="p-6 text-slate-500" data-testid="lead-loading">Loading dashboard…</div>;
    if (!data) return <div className="p-6 text-rose-700">No data.</div>;

    const fy = data.fytd_totals || {};
    const hc = data.headcount_snapshot || {};
    const cpfte = data.cost_per_fte || {};
    const dept = data.department_cost || {};
    const regime = data.regime_mix || {};

    return (
        <div className="space-y-4" data-testid="payroll-leadership-dashboard">
            <PageHeader
                eyebrow="Payroll Dashboards · Leadership"
                title={`Leadership · ${periodMonth}`}
                description={`FY ${data.fy} · ${(fy.months_included || []).length} months in scope · 24-month YoY view`}
                actions={(
                    <div className="flex items-center gap-2">
                        <Input
                            type="month"
                            value={periodMonth}
                            onChange={(e) => setPeriodMonth(e.target.value)}
                            className="h-9 w-40"
                            data-testid="lead-period-picker"
                        />
                        <Button variant="outline" size="sm" onClick={reload} data-testid="lead-refresh-btn">
                            <ArrowsClockwise size={14} className="mr-1" /> Refresh
                        </Button>
                    </div>
                )}
            />

            {/* FYTD Headline */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="lead-fytd-headline">
                <div className="border rounded-lg bg-white p-4" data-testid="lead-fytd-gross">
                    <div className="text-[11px] uppercase text-slate-500">FYTD Gross</div>
                    <div className="text-2xl font-mono font-semibold mt-0.5">{compact(fy.gross_earnings)}</div>
                </div>
                <div className="border rounded-lg bg-white p-4" data-testid="lead-fytd-net">
                    <div className="text-[11px] uppercase text-slate-500">FYTD Net Paid</div>
                    <div className="text-2xl font-mono font-semibold mt-0.5">{compact(fy.net_pay)}</div>
                </div>
                <div className="border rounded-lg bg-white p-4" data-testid="lead-fytd-ctc">
                    <div className="text-[11px] uppercase text-slate-500">FYTD CTC</div>
                    <div className="text-2xl font-mono font-semibold mt-0.5">{compact(fy.ctc_for_period)}</div>
                </div>
                <div className="border rounded-lg bg-white p-4" data-testid="lead-fytd-employer">
                    <div className="text-[11px] uppercase text-slate-500">FYTD Employer</div>
                    <div className="text-2xl font-mono font-semibold mt-0.5">{compact(fy.employer_contributions)}</div>
                </div>
            </div>

            {/* Headcount + Cost per FTE + Regime mix */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="border rounded-lg bg-white p-4" data-testid="lead-headcount">
                    <div className="flex items-center gap-1.5 mb-2">
                        <Users size={14} className="text-slate-400" />
                        <div className="text-xs uppercase text-slate-500 tracking-wide">Headcount</div>
                    </div>
                    <div className="text-3xl font-mono font-semibold" data-testid="lead-headcount-total">{hc.total_active || 0}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Active + Probation as of {hc.on_date}</div>
                    <div className="mt-3 space-y-0.5 text-xs">
                        {(hc.breakdown || []).map((b) => (
                            <div key={b.status} className="flex justify-between" data-testid={`lead-headcount-${b.status.replace(/\s+/g, '-')}`}>
                                <span className="text-slate-500">{b.status}</span>
                                <span className="font-mono">{b.count}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="border rounded-lg bg-white p-4" data-testid="lead-cost-per-fte">
                    <div className="flex items-center gap-1.5 mb-2">
                        <ChartBar size={14} className="text-slate-400" />
                        <div className="text-xs uppercase text-slate-500 tracking-wide">Cost per FTE ({periodMonth})</div>
                    </div>
                    <div className="text-3xl font-mono font-semibold" data-testid="lead-cpfte-mean">{compact(cpfte.mean)}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Mean monthly CTC across {cpfte.n || 0} FTEs</div>
                    <div className="mt-3 space-y-0.5 text-xs">
                        <div className="flex justify-between" data-testid="lead-cpfte-median"><span className="text-slate-500">Median</span><span className="font-mono">{compact(cpfte.median)}</span></div>
                        <div className="flex justify-between" data-testid="lead-cpfte-p25"><span className="text-slate-500">25th Percentile</span><span className="font-mono">{compact(cpfte.p25)}</span></div>
                        <div className="flex justify-between" data-testid="lead-cpfte-p75"><span className="text-slate-500">75th Percentile</span><span className="font-mono">{compact(cpfte.p75)}</span></div>
                        <div className="flex justify-between" data-testid="lead-cpfte-range"><span className="text-slate-500">Min · Max</span><span className="font-mono">{compact(cpfte.min)} · {compact(cpfte.max)}</span></div>
                    </div>
                </div>

                <div className="border rounded-lg bg-white p-4" data-testid="lead-regime">
                    <div className="flex items-center gap-1.5 mb-2">
                        <ChartPieSlice size={14} className="text-slate-400" />
                        <div className="text-xs uppercase text-slate-500 tracking-wide">Tax Regime Mix</div>
                    </div>
                    <RegimePie mix={regime} />
                </div>
            </div>

            {/* Department cost */}
            <div className="border rounded-lg bg-white p-4" data-testid="lead-department-cost">
                <div className="text-xs uppercase text-slate-500 tracking-wide mb-3">Cost by Department ({periodMonth})</div>
                {(dept.rows || []).length === 0 ? (
                    <div className="text-sm text-slate-400" data-testid="lead-dept-empty">No locked runs in this period yet.</div>
                ) : (
                    <DeptStack rows={dept.rows || []} />
                )}
            </div>

            {/* 24-month trend */}
            <div className="border rounded-lg bg-white p-4" data-testid="lead-trend-24">
                <div className="flex items-center gap-1.5 mb-3">
                    <ChartLineUp size={14} className="text-slate-400" />
                    <div className="text-xs uppercase text-slate-500 tracking-wide">24-month Gross Payroll Trend</div>
                </div>
                <TrendBars series={data.trend_24_months || []} testId="lead-trend-bars" />
            </div>
        </div>
    );
}
